import React from 'react';
import dynamic from 'next/dynamic';

import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { defaultNS } from 'next-i18next.config';
import { mapNamedLocaleToLocale } from 'lib/data/localesMap';
import generateHreflang from 'lib/utils/generateHreflang';
import PATHS from 'lib/navigation/paths';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { SLUGS_TYPES } from 'lib/utils/CONSTANTS';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import { HydrationWrapper } from 'views/common/components';

import { handleServerResponse } from 'views/deals-module/utils/handleServerResponse';
import { getDeviceType } from 'lib/utils/getDeviceType';
import { checkIfUrlHasCorrectSegmentsTranslations } from 'lib/utils/checkIfUrlHasCorrectSegmentsTranslations';
import { ContentContext } from 'views/rebranded-module/common/context/useContent';
import { useDealsListingCMSData } from 'views/rebranded-module/deals-module/DealsViewV2/hooks/useDealsListingCMSData';
import { useDealDetailsCMSData } from 'views/rebranded-module/deals-module/DealDetailsViewV2/hooks/useDealDetailsCMSData';

const DealsViewV2 = dynamic(() => import('views/rebranded-module/deals-module/DealsViewV2').then(
  (file) => file.DealsViewV2,
));
const DealDetailsViewV2 = dynamic(() => import('views/rebranded-module/deals-module/DealDetailsViewV2').then(
  (file) => file.DealDetailsViewV2,
));

const Deals = (props) => {
  const {
    serverData,
    renderErrorPage,
    serverError,
    slugTypeSSR,
    rentalWidgetInit,
  } = props;

  const appNavigator = useAppNavigator();
  const handleErrorResponse = useErrorsHandler();
  const content = useDealsListingCMSData();
  const dealDetailsContent = useDealDetailsCMSData();

  if (renderErrorPage) {
    handleErrorResponse({}, false);
  }
  // eslint-disable-next-line consistent-return
  const renderPage = () => {
    if (
      !appNavigator.query.slug
      || (slugTypeSSR
        && slugTypeSSR.slugs
        && slugTypeSSR.slugs[0].slug_type !== SLUGS_TYPES.deal)
    ) {
      return (
        <ContentContext.Provider value={content?.data}>
          <DealsViewV2 serverData={serverData} slugData={slugTypeSSR} />
        </ContentContext.Provider>
      );
    }
    if (
      slugTypeSSR
      && slugTypeSSR.slugs
      && slugTypeSSR.slugs[0].slug_type === SLUGS_TYPES.deal
    ) {
      return (
        <ContentContext.Provider value={dealDetailsContent?.data}>
          <DealDetailsViewV2
            serverData={serverData}
            slugData={slugTypeSSR}
            rentalWidgetInit={rentalWidgetInit}
          />
        </ContentContext.Provider>
      );
    }
  };

  return <HydrationWrapper>{!serverError && renderPage()}</HydrationWrapper>;
};

export async function getServerSideProps(ctx) {
  const { locale, query } = ctx;

  const locationSlug = query.location_slug
    ? `${query.slug}/${query.location_slug}`
    : query.slug;
  let serverData = null;
  const renderErrorPage = false;
  let hreflangData;
  const pageOptions = {
    rebrandingPage: true,
    options: {
      transparentNavBar: true,
    },
  };
  if (query.slug) {
    hreflangData = generateHreflang(
      PATHS.motorcycleDeals.slug(locationSlug).url,
      locale,
    );
  } else {
    hreflangData = generateHreflang(PATHS.motorcycleDeals.index().url, locale);
  }
  serverData = await handleServerResponse(ctx, hreflangData.canonical);
  if (serverData.newPath) {
    return {
      redirect: {
        destination: serverData.newPath,
        permanent: true,
      },
    };
  }

  if (serverData.redirectURL) {
    return {
      redirect: {
        destination: serverData.redirectURL,
        permanent: true,
      },
    };
  }
  if (serverData.serverError) {
    ctx.res.statusCode = serverData.serverError.statusCode;
  }

  const deviceType = getDeviceType(ctx.req.headers['user-agent']);

  const isCorrectSegemntTranslation = checkIfUrlHasCorrectSegmentsTranslations(
    ctx,
    hreflangData.canonical,
  );
  if (isCorrectSegemntTranslation.shouldRedirect) {
    return {
      redirect: {
        destination: isCorrectSegemntTranslation.redirectURL,
        permanent: true,
      },
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(mapNamedLocaleToLocale[locale], [
        ...defaultNS,
        'deals_listing',
        'common_deal_card',
        'fe_er_best_price_guarantee',
        'fe_er_expandable_description_component',
        'deal_details',
        'common_share',
        'common_participating_locations',
        'fe_er_deal_card',
        'fe_er_expandable_description_component',
        'common_new_main_widget',
        'fe_er_common_main_widget',
        'fe_er_common_location_card',
        'fe_er_common_map_with_groups',
        'fe_er_tour_package_selector',
        'fe_er_share_component',
        'fe_er_common_app_ad',
        'common_tour_footnote',
        'common_deal_card',
        'common_tour_booking_widget',
      ])),
      hreflangData,
      ...serverData,
      ...pageOptions,
      renderErrorPage,
      deviceType,
    },
  };
}
export default Deals;
